<template>
    <div class="max-w-2xl mx-auto p-4">
        <h1 class="text-2xl font-semibold text-theme-base mb-2">
            Métodos de pago del checkout
        </h1>
        <p class="text-sm text-theme-muted mb-6">
            Métodos de pago disponibles para tus clientes en el catálogo
            público. No afecta los métodos de pago internos del portal, que se
            configuran en
            <router-link
                to="/app/settings/payment-methods"
                class="text-theme-primary underline"
            >
                Métodos de pago del portal </router-link
            >.
        </p>

        <div class="bg-theme-card rounded-xl p-6 flex flex-col gap-8">
            <section class="flex flex-col gap-1">
                <label class="flex items-center gap-2">
                    <custom-checkbox :checked="hasPickupMerchants" disabled />
                    <span class="text-sm text-theme-base">
                        Entrega en tienda
                    </span>
                </label>
                <button
                    v-if="hasPickupMerchants"
                    type="button"
                    class="self-start text-sm text-theme-primary underline"
                    @click="isOpenPickupMerchantsModal = true"
                >
                    Ver tiendas ({{ pickupMerchants.length }})
                </button>
                <span v-else class="text-sm text-theme-muted">
                    Para activarla, habilita entregas en al menos una
                    <router-link
                        to="/app/stores"
                        class="text-theme-primary underline"
                        >tienda</router-link
                    >
                    o
                    <router-link
                        to="/app/warehouses"
                        class="text-theme-primary underline"
                        >bodega</router-link
                    >.
                </span>
            </section>

            <section>
                <div class="flex flex-wrap gap-3">
                    <label
                        v-for="method in availablePaymentMethods"
                        :key="method.value"
                        class="flex items-center gap-2 cursor-pointer"
                    >
                        <custom-checkbox
                            :value="method.value"
                            :checked="
                                settings.paymentMethodsAvailable.includes(
                                    method.value
                                )
                            "
                            @change="togglePaymentMethod(method.value)"
                        />
                        <span class="text-sm text-theme-base">{{
                            method.label
                        }}</span>
                    </label>
                </div>
            </section>

            <div class="flex justify-end pt-2">
                <app-button :loading="isLoading" @click="save">
                    Actualizar
                </app-button>
            </div>
        </div>

        <app-dialog v-model="isOpenPickupMerchantsModal" size="sm">
            <template #header>
                <h3 class="text-xl">Tiendas con entrega</h3>
            </template>
            <ul class="flex flex-col gap-3">
                <li
                    v-for="merchant in pickupMerchants"
                    :key="`${merchant.merchantType}:${merchant.id}`"
                    class="flex flex-col"
                >
                    <span class="font-semibold text-theme-base">
                        {{ merchant.name }}
                    </span>
                    <span class="text-sm text-theme-muted">
                        {{ merchant.address }}
                    </span>
                </li>
            </ul>
            <p class="text-sm text-theme-muted mt-4">
                Las entregas se habilitan o deshabilitan desde
                <router-link
                    to="/app/stores"
                    class="text-theme-primary underline"
                    >tiendas</router-link
                >
                y
                <router-link
                    to="/app/warehouses"
                    class="text-theme-primary underline"
                    >bodegas</router-link
                >.
            </p>
        </app-dialog>
    </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import { paymentMethodsCodes } from "@/common/fields/orders-payment-methods";
import {
    defaultOrdersSettings,
    mapOrdersSettingsFromApi,
    mapOrdersSettingsToApi
} from "@/common/fields/orders-settings";

import AppDialog from "@/components/utils/AppDialog.vue";

export default {
    name: "CheckoutPaymentMethodsSettingsPage",
    components: { AppDialog },
    layout: "app",
    data: () => ({
        isLoading: false,
        isOpenPickupMerchantsModal: false,
        pickupMerchants: [],
        availablePaymentMethods: Object.entries(paymentMethodsCodes).map(
            ([value, label]) => ({ value, label })
        ),
        settings: defaultOrdersSettings()
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        hasPickupMerchants() {
            return this.pickupMerchants.length > 0;
        }
    },
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        if (
            !(await verifyPagePermission(
                "tenant:GetOrdersSettings",
                "/app/settings"
            ))
        )
            return;
        await Promise.all([this.loadSettings(), this.loadPickupMerchants()]);
    },
    methods: {
        ...mapActions("tenants", [
            "getTenantOrdersSettings",
            "updateTenantOrdersSettings"
        ]),
        ...mapActions("stores", ["listStoresPaginated"]),
        ...mapActions("warehouses", ["listWarehousesPaginated"]),
        async loadPickupMerchants() {
            const query = {
                enabledForDeliveries: true,
                page: 1,
                itemsByPage: 50
            };
            try {
                const [stores, warehouses] = await Promise.all([
                    this.listStoresPaginated(query),
                    this.listWarehousesPaginated(query)
                ]);
                this.pickupMerchants = [
                    ...stores.items.map((item) => ({
                        ...item,
                        merchantType: "store"
                    })),
                    ...warehouses.items.map((item) => ({
                        ...item,
                        merchantType: "warehouse"
                    }))
                ];
            } catch (error) {
                // Sin la lista el check queda sin marcar; no bloquea los
                // métodos de pago, que son lo que se edita en esta pantalla.
                this.pickupMerchants = [];
            }
        },
        async loadSettings() {
            this.$loading.show();
            try {
                const data = await this.getTenantOrdersSettings();
                this.settings = mapOrdersSettingsFromApi(data);
                this.$loading.hide();
            } catch (error) {
                this.$loading.hide();
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        togglePaymentMethod(method) {
            const current = [...this.settings.paymentMethodsAvailable];
            const idx = current.indexOf(method);
            if (idx === -1) {
                current.push(method);
            } else {
                current.splice(idx, 1);
            }
            this.settings.paymentMethodsAvailable = current;
        },
        async save() {
            this.$loading.show();
            this.isLoading = true;
            try {
                await this.updateTenantOrdersSettings(
                    mapOrdersSettingsToApi(this.settings)
                );
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.$loading.hide();
                this.isLoading = false;
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        }
    }
};
</script>
